import { useEffect } from 'react';
import { usePage } from '@inertiajs/react';

import { getDocument } from '@/lib/utils';
import type { Page } from '@/types/page';
import type { SharedData, Site } from '@/types/shared';

// TODO: Explore sandboxing preview iframe or using a separate origin to isolate custom scripts from the CMS context.

type HtmlInjection = {
  nodes: ChildNode[];
  done: Promise<void>;
  remove: () => void;
};

const documentInjectionQueues = new WeakMap<Document, Promise<void>>();
const ASSET_LOAD_TIMEOUT_MS = 15_000;

function isBlockingExternalScript(node: ChildNode): boolean {
  const element = node.nodeType === 1 ? (node as HTMLElement) : null;
  const script = element?.tagName === 'SCRIPT' ? (element as HTMLScriptElement) : null;
  return Boolean(script?.src) && !script?.hasAttribute('async');
}

function waitForAsset(node: ChildNode, signal: AbortSignal): Promise<void> {
  if (!isBlockingExternalScript(node)) return Promise.resolve();

  return new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timeout);
      node.removeEventListener('load', finish);
      node.removeEventListener('error', finish);
      signal.removeEventListener('abort', finish);
      resolve();
    };
    const timeout = setTimeout(finish, ASSET_LOAD_TIMEOUT_MS);
    node.addEventListener('load', finish, { once: true });
    node.addEventListener('error', finish, { once: true });
    signal.addEventListener('abort', finish, { once: true });
  });
}

/**
 * Adds the imported scripts to the page in order, waiting for each external
 * script to load before adding what follows it. Legacy scripts frequently depend
 * on one loaded just before them, so running them out of order breaks them.
 */
export function injectHtml(html: string, target: HTMLElement): HtmlInjection {
  const range = target.ownerDocument.createRange();
  range.selectNode(target);
  const fragment = range.createContextualFragment(html);
  const pendingNodes = Array.from(fragment.childNodes);
  const injected: ChildNode[] = [];
  const abortController = new AbortController();
  const previous = documentInjectionQueues.get(target.ownerDocument) ?? Promise.resolve();

  const done = previous
    .catch(() => undefined)
    .then(async () => {
      for (const node of pendingNodes) {
        if (abortController.signal.aborted) break;

        const loaded = waitForAsset(node, abortController.signal);
        injected.push(node);
        target.append(node);
        await loaded;
      }
    });

  documentInjectionQueues.set(target.ownerDocument, done);

  return {
    nodes: injected,
    done,
    remove: () => {
      abortController.abort();
      for (const node of injected) node.remove();
    },
  };
}

function parsingDone(activeDocument: Document): Promise<void> {
  if (activeDocument.readyState !== 'loading') return Promise.resolve();

  return new Promise((resolve) => {
    activeDocument.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
  });
}

function queueAdoptedScripts(target: HTMLElement, adopted: ChildNode[]): void {
  if (!adopted.some(isBlockingExternalScript)) return;

  const activeDocument = target.ownerDocument;
  const parsed = parsingDone(activeDocument);
  const previous = documentInjectionQueues.get(activeDocument) ?? Promise.resolve();
  documentInjectionQueues.set(
    activeDocument,
    previous.catch(() => undefined).then(() => parsed),
  );
}

function pickScriptValue(
  source: { customScripts?: Site['customScripts'] } | null | undefined,
  key: 'headerScripts' | 'footerScripts',
): string {
  const value = source?.customScripts?.[key];
  return typeof value === 'string' ? value : '';
}

function useResolvedScripts() {
  const activeDocument = getDocument();
  const { props, url } = usePage<
    SharedData & {
      site?: Site;
      page?: Page | null;
    }
  >();
  const { site, page: pageFromProps } = props;
  const page = pageFromProps ?? null;
  return {
    activeDocument,
    visit: url,
    site: {
      headerScripts: pickScriptValue(site, 'headerScripts'),
      footerScripts: pickScriptValue(site, 'footerScripts'),
    },
    page: {
      headerScripts: pickScriptValue(page, 'headerScripts'),
      footerScripts: pickScriptValue(page, 'footerScripts'),
    },
  };
}

// Claims the scripts the server rendered into <head> so the browser runs them before hydration.
// Null means no markers: an Inertia visit, a footer scope, or a fragment the server withheld.
function adoptServerScripts(target: HTMLElement, key: string): ChildNode[] | null {
  const start = target.querySelector(`template[data-custom-scripts-start="${key}"]`);
  const end = target.querySelector(`template[data-custom-scripts-end="${key}"]`);
  if (!start || !end) return null;

  const nodes: ChildNode[] = [];
  let node = start.nextSibling;
  while (node && node !== end) {
    nodes.push(node);
    node = node.nextSibling;
  }
  start.remove();
  end.remove();

  return nodes;
}

// Keeps one set of scripts installed: adopted from the server HTML, or injected on an Inertia visit.
function useCustomScripts(
  html: string,
  target: HTMLElement | undefined,
  key: string,
  lifetimeKey: string,
) {
  // biome-ignore lint/correctness/useExhaustiveDependencies: The caller selects each script scope's lifecycle.
  useEffect(() => {
    if (!target || !html) return;

    // StrictMode reinjects server scripts during development. Keeping this hook stateless
    // avoids retaining nodes across URL changes, where page scripts must run again.
    const adopted = adoptServerScripts(target, key);
    if (!adopted) return injectHtml(html, target).remove;

    queueAdoptedScripts(target, adopted);

    return () => {
      for (const node of adopted) node.remove();
    };
  }, [html, target, key, lifetimeKey]);
}

// Owns the site and page header scripts in <head>: adopted from the server HTML, swapped on Inertia visits.
// JSON-LD structured data is emitted server-side via InertiaMetaBuilder.
export function CustomHeaderScripts() {
  const { activeDocument, visit, site: siteScripts, page: pageScripts } = useResolvedScripts();

  useCustomScripts(siteScripts.headerScripts, activeDocument?.head, 'site-header', visit);
  useCustomScripts(pageScripts.headerScripts, activeDocument?.head, 'page-header', visit);

  return null;
}

/**
 * Injects site-level and page-level footer scripts at the end of the document body.
 * - Site footer scripts
 * - Page footer scripts
 */
export function CustomFooterScripts() {
  const { activeDocument, visit, site: siteScripts, page: pageScripts } = useResolvedScripts();

  useCustomScripts(siteScripts.footerScripts, activeDocument?.body, 'site-footer', visit);
  useCustomScripts(pageScripts.footerScripts, activeDocument?.body, 'page-footer', visit);

  return null;
}
